package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import org.w3c.files.File

private const val EULA_URL = "https://writers-lab.what2read.space/eula"

/**
 * Empty drop zone — clicked to open the file picker, or files can be dragged
 * onto it. The dragover counter prevents flicker as the cursor crosses
 * children: HTML fires dragenter/dragleave on every child crossing, so we
 * count enters minus leaves and only un-highlight when we hit zero.
 */
@Composable
fun UploadArea(onPickFile: () -> Unit, onDropFile: (File) -> Unit) {
    var isDragOver by remember { mutableStateOf(false) }
    var dragDepth by remember { mutableStateOf(0) }
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerHigh, radiusPx = 8, paddingPx = 0)
            property(
                "background-color",
                if (isDragOver) "rgba(165, 174, 247, 0.18)" else "rgba(43, 40, 50, 0.5)"
            )
            border(
                "2px",
                if (isDragOver) "solid" else "solid",
                if (isDragOver) WLColors.Primary else "rgba(165, 174, 247, 0.3)"
            )
            property("min-height", "180px")
            property("padding", "16px 24px")
            fillWidth()
            flexCol(gapPx = 8)
            justifyContent(JustifyContent.Center)
            property("align-items", "center")
            property("text-align", "center")
            clickableCursor()
            property("box-sizing", "border-box")
            property("transition", "background-color 0.15s ease, border-color 0.15s ease")
        }
        onClick { onPickFile() }
        ref { node ->
            val onDragEnter: (Event) -> Unit = { ev ->
                ev.preventDefault()
                dragDepth += 1
                isDragOver = true
            }
            val onDragOver: (Event) -> Unit = { ev ->
                ev.preventDefault()
                ev.asDynamic().dataTransfer.dropEffect = "copy"
            }
            val onDragLeave: (Event) -> Unit = { ev ->
                ev.preventDefault()
                dragDepth -= 1
                if (dragDepth <= 0) {
                    dragDepth = 0
                    isDragOver = false
                }
            }
            val onDrop: (Event) -> Unit = { ev ->
                ev.preventDefault()
                dragDepth = 0
                isDragOver = false
                val files = ev.asDynamic().dataTransfer.files.unsafeCast<dynamic>()
                if (files != null && files.length.unsafeCast<Int>() > 0) {
                    val file = files[0].unsafeCast<File>()
                    onDropFile(file)
                }
            }
            node.addEventListener("dragenter", onDragEnter)
            node.addEventListener("dragover", onDragOver)
            node.addEventListener("dragleave", onDragLeave)
            node.addEventListener("drop", onDrop)
            onDispose {
                node.removeEventListener("dragenter", onDragEnter)
                node.removeEventListener("dragover", onDragOver)
                node.removeEventListener("dragleave", onDragLeave)
                node.removeEventListener("drop", onDrop)
            }
        }
    }) {
        // Inline SVG of the upload-arrow-with-tray icon — same shape as the
        // previous Compose Skia icon. We attach it via `ref` so Compose
        // doesn't try to manage the SVG children itself.
        Div({
            style {
                property("width", "48px")
                property("height", "48px")
            }
            ref { node ->
                node.innerHTML = """<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"
                  viewBox="0 0 24 24" fill="none" stroke="${WLColors.Primary}"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>"""
                onDispose { node.innerHTML = "" }
            }
        })
        Span({
            style {
                typography(TextLevel.TitleMedium)
                fg(WLColors.OnSurface)
            }
        }) { Text(LocalStrings.current.uploadTitle) }
        Span({
            style {
                typography(TextLevel.BodySmall)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(LocalStrings.current.uploadFormats) }
        // Scope hint — calls out the failure mode users can't see in
        // advance: feeding in a short-story anthology or a "trilogy
        // in one file" of unrelated novellas yields metrics that are
        // averages across mismatched works, which reads as a low or
        // confusing score. Better to warn before upload than to
        // mystify after.
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
                property("max-width", "560px")
                property("padding-top", "4px")
            }
        }) { Text(LocalStrings.current.uploadScope) }
        // Privacy / IP assurance — surfaces the three things writers
        // worry about before uploading a manuscript: who else sees it,
        // what we do with it, who owns the output. Wording deliberately
        // covers all three in a single sentence; placement inside the
        // drop zone keeps the reassurance attached to the action.
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
                property("max-width", "560px")
                property("padding-top", "4px")
            }
        }) { Text(LocalStrings.current.uploadPrivacy) }
    }
}

/** Filled card shown after a file has been picked (inside results flow). */
@Composable
fun UploadedFileCard(
    filename: String,
    wordCount: Int,
    onDownloadTxt: () -> Unit,
    onReset: () -> Unit,
) {
    val s = LocalStrings.current
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerHigh, radiusPx = 8, paddingPx = 16)
            property("padding", "16px 24px")
            flexRow(gapPx = 12)
            justifyContent(JustifyContent.SpaceBetween)
            fillWidth()
            property("box-sizing", "border-box")
        }
    }) {
        Div({ style { flexRow(gapPx = 12) } }) {
            Span({
                style {
                    typography(TextLevel.TitleMedium)
                    fg(WLColors.Success)
                    bg("rgba(165, 174, 247, 0.15)")
                    property("border-radius", "8px")
                    property("padding", "8px")
                }
            }) { Text("+") }
            Div({ style { flexCol() } }) {
                Span({
                    style {
                        typography(TextLevel.TitleMedium)
                        fg(WLColors.OnSurface)
                    }
                }) { Text(filename) }
                if (wordCount > 0) {
                    Span({
                        style {
                            typography(TextLevel.BodySmall)
                            fg(WLColors.OnSurfaceVariant)
                        }
                    }) { Text("${formatWordCount(wordCount)} ${s.words}") }
                }
            }
        }
        Div({ style { flexRow(gapPx = 4) } }) {
            Div({
                style {
                    typography(TextLevel.LabelLarge)
                    fg(WLColors.OnSurfaceVariant)
                    clickableCursor()
                    property("padding", "8px 12px")
                    property("border-radius", "6px")
                }
                onClick { onDownloadTxt() }
            }) {
                Text(".txt")
            }
            Div({
                style {
                    typography(TextLevel.LabelLarge)
                    fg(WLColors.Primary)
                    clickableCursor()
                    property("padding", "8px 12px")
                    property("border-radius", "6px")
                }
                onClick { onReset() }
            }) {
                Text(s.change)
            }
        }
    }
}

/**
 * EULA consent row for anonymous uploads. Highlights red when [error]
 * is true (the user clicked the upload area without checking the box).
 */
@Composable
fun EulaConsentRow(
    checkedValue: Boolean,
    error: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    val s = LocalStrings.current
    Div({
        style {
            flexRow(gapPx = 8, align = AlignItems.Center)
            property("padding", "4px")
            border("1px", "solid", if (error) WLColors.Error else "transparent")
            property("border-radius", "8px")
        }
    }) {
        CheckboxInput(checked = checkedValue) {
            onChange { event -> onCheckedChange(event.value) }
            attr("style", "accent-color: ${WLColors.Primary}; transform: scale(1.2); margin: 0 6px;")
        }
        Span({
            style {
                typography(TextLevel.BodySmall)
                fg(if (error) WLColors.Error else WLColors.OnSurfaceVariant)
            }
        }) { Text(s.eulaPrefix) }
        Div({
            style {
                typography(TextLevel.BodySmall)
                fg(WLColors.Primary)
                property("text-decoration", "underline")
                clickableCursor()
                property("padding", "0 4px")
            }
            onClick { jsOpenInNewTab(EULA_URL) }
        }) {
            Text(s.eulaLink)
        }
    }
}

private fun formatWordCount(n: Int): String {
    val s = n.toString()
    val sb = StringBuilder()
    for ((i, c) in s.reversed().withIndex()) {
        if (i > 0 && i % 3 == 0) sb.append(' ')
        sb.append(c)
    }
    return sb.reverse().toString()
}
